
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigReject} from './ConfigReject';
import {FieldModelConfigReject} from './ConfigReject';

/**
 * ConfigUpdateReject struct
 */
class ConfigUpdateReject extends ConfigReject {
  /**
   * Initialize struct
   * @param {!ConfigReject=} parent
   * @param {UUID=} updateId
   * @param {number=} updateVersion
   * @param {!string=} comment
   * @constructor
   */
  constructor (parent = new ConfigReject(), updateId = null, updateVersion = null, comment = '') {
    super()
    super.copy(parent)
    this.UpdateId = updateId
    this.UpdateVersion = updateVersion
    this.Comment = comment
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigUpdateReject}
   * @param {!ConfigUpdateReject} other Other struct
   * @returns {!ConfigUpdateReject} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.UpdateId != null) {
      this.UpdateId = new UUID(other.UpdateId)
    } else {
      this.UpdateId = null
    }
    if (other.UpdateVersion != null) {
      this.UpdateVersion = UInt64.fromNumber(other.UpdateVersion)
    } else {
      this.UpdateVersion = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigUpdateReject}
   * @returns {!ConfigUpdateReject} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigUpdateRejectModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigUpdateRejectModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigUpdateReject}
   * @param {!ConfigUpdateReject} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigUpdateReject)) {
      throw new TypeError('Instance of ConfigUpdateReject is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigUpdateReject}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      UpdateId: ((this.UpdateId != null) ? this.UpdateId.toString() : null),
      UpdateVersion: ((this.UpdateVersion != null) ? this.UpdateVersion.toNumber() : null),
      Comment: ((this.Comment != null) ? this.Comment : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigUpdateReject.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigUpdateReject} other Object value
   * @returns {!ConfigUpdateReject} Created struct
   */
  static fromObject (other) {
    return new ConfigUpdateReject().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigUpdateReject}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigUpdateReject.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigUpdateReject}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 1017
  }
}

export { ConfigUpdateReject };

/**
 * Fast Binary Encoding ConfigUpdateReject field model
 */
class FieldModelConfigUpdateReject extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigReject(buffer, 4 + 4)
    this.UpdateId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.UpdateVersion = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.UpdateId.fbeOffset + this.UpdateId.fbeSize), buffer, this.UpdateId.fbeOffset + this.UpdateId.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.UpdateVersion.fbeOffset + this.UpdateVersion.fbeSize)
  }

  /**
   * Get the ConfigReject field model
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!FieldModelConfigReject} ConfigReject field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.UpdateId.fbeSize + this.UpdateVersion.fbeSize + this.Comment.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.UpdateId.fbeExtra + this.UpdateVersion.fbeExtra + this.Comment.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigUpdateReject.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 1017
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigUpdateReject}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigUpdateReject}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.UpdateId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdateId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateId.fbeSize

    if ((fbeCurrentSize + this.UpdateVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UpdateVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateVersion.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigUpdateReject}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigUpdateReject}
   * @param {!ConfigUpdateReject} fbeValue Default value, defaults is new ConfigUpdateReject()
   * @returns {!ConfigUpdateReject} ConfigUpdateReject value
   */
  get (fbeValue = new ConfigUpdateReject()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigUpdateReject}
   * @param {!ConfigUpdateReject} fbeValue ConfigUpdateReject value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.UpdateId.fbeSize) <= fbeStructSize) {
      fbeValue.UpdateId = this.UpdateId.get()
    } else {
      fbeValue.UpdateId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateId.fbeSize

    if ((fbeCurrentSize + this.UpdateVersion.fbeSize) <= fbeStructSize) {
      fbeValue.UpdateVersion = this.UpdateVersion.get()
    } else {
      fbeValue.UpdateVersion = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UpdateVersion.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigUpdateReject}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigUpdateReject}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigUpdateReject}
   * @param {!ConfigUpdateReject} fbeValue ConfigUpdateReject value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigUpdateReject}
   * @param {ConfigUpdateReject} fbeValue ConfigUpdateReject value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.UpdateId.set(fbeValue.UpdateId)
    this.UpdateVersion.set(fbeValue.UpdateVersion)
    this.Comment.set(fbeValue.Comment)
  }
}

export { FieldModelConfigUpdateReject };

/**
 * Fast Binary Encoding ConfigUpdateReject model
 */
class ConfigUpdateRejectModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigUpdateReject(this.buffer, 4)
  }

  /**
   * Get the ConfigUpdateReject model
   * @this {!ConfigUpdateRejectModel}
   * @returns {!FieldModelConfigUpdateReject} model ConfigUpdateReject model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigUpdateRejectModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigUpdateRejectModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigUpdateRejectModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigUpdateRejectModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigUpdateReject.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigUpdateRejectModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigUpdateRejectModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigUpdateRejectModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigUpdateRejectModel}
   * @param {!ConfigUpdateReject} value ConfigUpdateReject value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigUpdateRejectModel}
   * @param {!ConfigUpdateReject} value ConfigUpdateReject value, defaults is new ConfigUpdateReject()
   * @return {!object} Deserialized ConfigUpdateReject value and its size
   */
  deserialize (value = new ConfigUpdateReject()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigUpdateReject(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigUpdateReject(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigUpdateRejectModel}
   * @param {!number} prev Previous ConfigUpdateReject model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigUpdateRejectModel };
